/* Recipe page behaviour: the reveal slider, share, add-to-cart and the
   "try more" ring. Registered on window so Alpine can call them from x-data.
   Loaded synchronously by the section, before Alpine walks the DOM. */
(function () {
  'use strict';
  if (window.recipePage) return;

  /* The reveal. The bottle sits underneath; the dish is clipped to everything
     left of the divider. inset() is used rather than two absolutely sized
     layers so the images never reflow -- only the clip rectangle changes,
     which stays on the compositor and does not thrash layout while dragging.

     `start` is where the divider rests before anyone touches it, as a
     percentage of the frame. It is a parameter rather than a constant because
     the best resting point is a judgement about the artwork -- how much of
     the bottle should be showing when the page loads -- and it is being tried
     on one recipe before the rest. 55 is the value every other recipe used
     before the option existed, so leaving it out changes nothing. */
  window.recipeReveal = function (cfg) {
    cfg = cfg || {};
    var start = typeof cfg.start === 'number' ? cfg.start : 55;
    return {
      pos: Math.max(0, Math.min(100, start)),
      dragging: false,
      clip: function () { return 'inset(0 ' + (100 - this.pos) + '% 0 0)'; },
      from: function (e) {
        var r = this.$refs.frame.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
        return Math.max(0, Math.min(100, (x / r.width) * 100));
      },
      start: function (e) { this.dragging = true; this.pos = this.from(e); },
      move: function (e) {
        if (!this.dragging) return;
        /* Only once dragging has begun, so a plain vertical page scroll that
           happens to start on the frame is not swallowed. */
        if (e.cancelable) e.preventDefault();
        this.pos = this.from(e);
      },
      end: function () { this.dragging = false; },
      /* Keyboard parity: the handle is a slider, so arrows and Home/End move
         it. Without this the reveal is unreachable without a pointer. */
      key: function (d) { this.pos = Math.max(0, Math.min(100, this.pos + d)); }
    };
  };

  window.recipeShare = function (cfg) {
    return {
      msg: '',
      async share() {
        var data = { title: cfg.title, text: cfg.text, url: cfg.url };
        /* navigator.share needs a secure context and a user gesture, and is
           absent on most desktops -- hence the clipboard fallback rather than
           a feature-detect that silently does nothing. */
        if (navigator.share) {
          try { await navigator.share(data); return; }
          catch (err) { if (err && err.name === 'AbortError') return; }
        }
        try {
          await navigator.clipboard.writeText(cfg.url);
          this.flash('Link copied');
        } catch (err) {
          this.flash('Copy this link: ' + cfg.url);
        }
      },
      flash: function (m) {
        var self = this;
        this.msg = m;
        clearTimeout(this._t);
        this._t = setTimeout(function () { self.msg = ''; }, 2600);
      }
    };
  };

  window.recipeAtc = function (cfg) {
    return {
      busy: false,
      done: false,
      label: cfg.label || 'Add to cart',
      async add() {
        if (this.busy || !cfg.variantId) return;
        this.busy = true;
        try {
          var res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(cfg.variantId), quantity: 1 }] })
          });
          if (!res.ok) throw new Error('add failed');
          this.busy = false;
          this.done = true;
          document.dispatchEvent(new CustomEvent('cart:updated'));
          /* Open the drawer if the theme's store is up; the item is in the
             cart either way, so a missing store must not look like a failure. */
          try { window.Alpine.store('modals').open('cart'); } catch (e) {}
          var self = this;
          setTimeout(function () { self.done = false; }, 2200);
        } catch (e) {
          this.busy = false;
          /* No silent failure: the product page can always complete the job. */
          if (cfg.productUrl) window.location.href = cfg.productUrl;
        }
      }
    };
  };

  /* A single-row version of the cookbook ring. Same shortest-signed-distance
     geometry, so it behaves at any count including one. */
  window.recipeRing = function (cfg) {
    return {
      items: cfg.items,
      i: 0,
      timer: null,
      resumeTimer: null,
      dragX: null,
      dragY: null,
      swiped: false,
      reduced: false,
      autoplayMs: cfg.autoplayMs || 2500,
      resumeMs: cfg.resumeMs || 5000,

      init: function () {
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!this.reduced) this.play();
      },
      n: function () { return this.items.length; },
      offset: function (id) {
        var n = this.n();
        if (!n) return null;
        var pos = -1;
        for (var k = 0; k < n; k++) { if (this.items[k].id === id) { pos = k; break; } }
        if (pos < 0) return null;
        var half = Math.floor(n / 2);
        return ((pos - (this.i % n) + n + half) % n) - half;
      },
      onStage: function (id) { var d = this.offset(id); return d !== null && Math.abs(d) <= 2; },
      isActive: function (id) { return this.offset(id) === 0; },
      isFar: function (id) { var d = this.offset(id); return d !== null && Math.abs(d) === 2; },
      cardStyle: function (id) {
        var d = this.offset(id);
        if (d === null) return 'display:none;';
        var s = d === 0 ? 1 : 0.58;
        var z = d === 0 ? 3 : (Math.abs(d) === 1 ? 2 : 1);
        return 'transform: translateX(calc(var(--ck-step) * ' + d + ')) scale(' + s + '); z-index:' + z + ';';
      },
      activeName: function () { return this.n() ? this.items[this.i % this.n()].name : ''; },
      step: function (d) {
        if (this.n() < 2) return;
        this.i = ((this.i + d) % this.n() + this.n()) % this.n();
        this.bump();
      },
      play: function () {
        var self = this;
        this.stop();
        if (this.reduced) return;
        this.timer = setInterval(function () { if (self.n() > 1) self.i = (self.i + 1) % self.n(); }, this.autoplayMs);
      },
      stop: function () { clearInterval(this.timer); this.timer = null; },
      bump: function () {
        var self = this;
        this.stop();
        clearTimeout(this.resumeTimer);
        if (this.reduced) { this.resumeTimer = null; return; }
        this.resumeTimer = setTimeout(function () { self.resumeTimer = null; self.play(); }, this.resumeMs);
      },
      down: function (e) { this.dragX = e.clientX; this.dragY = e.clientY; },
      up: function (e) {
        if (this.dragX === null) return;
        var dx = e.clientX - this.dragX, dy = e.clientY - this.dragY;
        this.dragX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          var self = this;
          this.swiped = true;
          setTimeout(function () { self.swiped = false; }, 60);
          this.step(dx < 0 ? 1 : -1);
        }
      },
      tap: function (id, e) {
        if (this.swiped) { e.preventDefault(); return; }
        if (!this.isActive(id)) {
          e.preventDefault();
          for (var k = 0; k < this.items.length; k++) { if (this.items[k].id === id) { this.i = k; break; } }
          this.bump();
        }
      }
    };
  };

  window.recipePage = true;
})();
